import Phaser from 'phaser';
import type { ExtractionResult, Chest, ChestReward, ItemRarity, PerkDefinition } from '@snake-royalle/shared';
import { calculateExtractionResult, getChestTierInfo, getRarityInfo } from '@snake-royalle/shared';
import { disableMenuScroll } from '../utils/menu-scroll.js';
import { sfx } from '../audio/SFX.js';
import type { CoinChart } from '../effects/CoinChart.js';
import { getRandomPerks } from '../data/perks.js';
import { getCachedHats, markHatOwned } from '../auth/hat-inventory.js';
import { getProvider } from '../auth/wallet-auth.js';

// API lives on the game server (Railway), not the page origin (Vercel).
const env = (import.meta as any).env;
const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

const RARITY_COLORS: Record<ItemRarity, number> = {
  COMMON: 0x888899,
  UNCOMMON: 0x22CC44,
  RARE: 0x4488FF,
  LEGENDARY: 0xFFD700,
};

interface ChestPayoutInfo {
  symbol: string;
  decimals: number;
  rateFemtoUsdcPerRaw: string;
  grantedRaw: string;
  granted: boolean;
}

/** Post-extraction ceremony — VS-style chest opening */
export class ChestCeremonyScene extends Phaser.Scene {
  private result!: ExtractionResult;
  private currentChestIndex: number = 0;
  private extractionsUsed: number = 0;
  private selectedPerk: any = null;
  private returnToGame: boolean = false;
  private coinChart: CoinChart | null = null;
  private rewardsServerGranted: boolean = false;
  /** Server-provided context for converting per-chest USD-micro into the
   *  native token the player actually receives. Null in demo/offline
   *  flows where there's no oracle round-trip. */
  private chestPayout: ChestPayoutInfo | null = null;

  constructor() {
    super({ key: 'ChestCeremonyScene' });
  }

  init(data: any): void {
    // Server-stamped payout context (forwarded by GameScene). Carries
    // the live token rate + symbol so we can render SNK per chest.
    this.chestPayout = data.result?.chestPayout ?? data.chestPayout ?? null;
    if (data.result?.chests) {
      this.result = data.result;
      this.rewardsServerGranted = true;
    } else {
      // Fallback for demo/offline flows. Paid games receive server-granted
      // rewards via `extraction_result` before this scene opens.
      this.result = calculateExtractionResult(
        data.coinsBanked ?? 0,
        data.coinsCollected ?? data.coinsBanked ?? 0,
        data.kills ?? 0,
        data.chainMax ?? 0,
        data.perkUsed ?? null,
        getCachedHats(),
      );
      this.rewardsServerGranted = false;
    }
    this.currentChestIndex = 0;
    this.extractionsUsed = data.extractionsUsed ?? 1;
    this.selectedPerk = data.selectedPerk ?? null;
    this.returnToGame = data.returnToGame ?? false;
    this.coinChart = data.coinChart ?? null;
  }

  create(): void {
    disableMenuScroll();
    this.cameras.main.setZoom(1);
    this.cameras.main.setScroll(0, 0);
    this.cameras.main.fadeIn(300);

    const { width, height } = this.scale;
    this.add.rectangle(width / 2, height / 2, width, height, 0x030308).setDepth(0);

    // Particle texture
    if (!this.textures.exists('chest_particle')) {
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(0, 0, 3, 3);
      g.generateTexture('chest_particle', 3, 3);
      g.destroy();
    }

    // Start with quick tally then chests
    this.showQuickTally();
  }

  // ============ QUICK TALLY (2-3 seconds) ============

  private showQuickTally(): void {
    const { width, height } = this.scale;
    const cx = width / 2;

    // "EXTRACTED!" slam
    const title = this.add.text(cx, height * 0.15, 'EXTRACTED!', {
      fontFamily: 'monospace', fontSize: '36px', color: '#00ff88',
      stroke: '#003322', strokeThickness: 4, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10).setScale(1.5).setAlpha(0);

    this.tweens.add({
      targets: title, alpha: 1, scale: 1, duration: 300, ease: 'Back.easeOut',
      onComplete: () => {
        sfx(this, 'title_slam');
        this.cameras.main.shake(60, 0.004);
      },
    });

    // Coins banked — big gold number rolling up. Multiplied by stake
    // for parity with the HUD ("BANKED 1000" for a x10 player with 100
    // raw coins). The chest-cost arithmetic below stays in RAW units
    // because that's what the chest tier thresholds (10/25/50/100)
    // are evaluated against on the server.
    const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;
    const displayedBanked = this.result.coinsBanked * stake;
    const stakeSuffix = stake > 1 ? ` ×${stake}` : '';
    const coinsLabel = this.add.text(cx, height * 0.32, `BANKED${stakeSuffix}`, {
      fontFamily: 'monospace', fontSize: '14px', color: '#888899',
    }).setOrigin(0.5).setDepth(10).setAlpha(0);

    const coinsValue = this.add.text(cx, height * 0.42, '0', {
      fontFamily: 'monospace', fontSize: '48px', color: '#ffd700',
      stroke: '#664400', strokeThickness: 3, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10).setAlpha(0);

    this.tweens.add({ targets: coinsLabel, alpha: 1, duration: 200, delay: 400 });
    this.tweens.add({ targets: coinsValue, alpha: 1, duration: 200, delay: 500 });

    // Roll up the number
    this.time.delayedCall(600, () => {
      sfx(this, 'coins_rolling');
      this.tweens.addCounter({
        from: 0, to: displayedBanked,
        duration: Math.min(1000, displayedBanked * 15),
        ease: 'Power2',
        onUpdate: (tween) => {
          coinsValue.setText(`${Math.floor(tween.getValue() as number)}`);
        },
        onComplete: () => {
          sfx(this, 'coins_punch');
          this.tweens.add({ targets: coinsValue, scaleX: 1.2, scaleY: 1.2, duration: 80, yoyo: true });
          this.cameras.main.shake(80, 0.005);
          this.cameras.main.flash(100, 255, 215, 0, false);
        },
      });
    });

    // Show chest costs + remaining
    if (this.result.chests.length > 0) {
      const spent = this.result.coinsSpent ?? 0;
      const remaining = this.result.coinsRemaining ?? 0;

      // "N CHESTS BOUGHT — spent X coins"
      const chestInfo = this.add.text(cx, height * 0.55,
        `${this.result.chests.length} CHEST${this.result.chests.length > 1 ? 'S' : ''} BOUGHT  (-${spent})`, {
        fontFamily: 'monospace', fontSize: '16px', color: '#ffd700',
        stroke: '#000', strokeThickness: 3,
      }).setOrigin(0.5).setDepth(10).setAlpha(0);

      this.tweens.add({ targets: chestInfo, alpha: 1, duration: 300, delay: 1800 });

      // Remaining coins
      if (remaining > 0) {
        const remainText = this.add.text(cx, height * 0.60, `${remaining} COINS SAVED`, {
          fontFamily: 'monospace', fontSize: '13px', color: '#00ff88',
          stroke: '#000', strokeThickness: 2,
        }).setOrigin(0.5).setDepth(10).setAlpha(0);

        this.tweens.add({ targets: remainText, alpha: 1, duration: 300, delay: 2000 });
      }
    }

    // Transition to first chest
    const delay = this.result.chests.length > 0 ? 2500 : 2000;
    this.time.delayedCall(delay, () => {
      // Fade out tally
      this.children.each((child: Phaser.GameObjects.GameObject) => {
        this.tweens.add({ targets: child, alpha: 0, duration: 300 });
      });
      this.time.delayedCall(400, () => {
        if (this.result.chests.length > 0) {
          this.showNextChest();
        } else {
          this.showHooks();
        }
      });
    });

    // Click to skip tally — show the multiplied final value to match
    // the counter's end state.
    this.input.once('pointerdown', () => {
      coinsValue.setText(`${displayedBanked}`);
    });
  }

  // ============ CHEST OPENING (one at a time, VS-style) ============

  private showNextChest(): void {
    if (this.currentChestIndex >= this.result.chests.length) {
      this.showHooks();
      return;
    }

    const chest = this.result.chests[this.currentChestIndex];
    const { width, height } = this.scale;
    const cx = width / 2;
    const tierInfo = getChestTierInfo(chest.tier);

    // Counter: "Chest 1/3"
    const counter = this.add.text(cx, height * 0.08, `CHEST ${this.currentChestIndex + 1} / ${this.result.chests.length}`, {
      fontFamily: 'monospace', fontSize: '14px', color: '#666677',
    }).setOrigin(0.5).setDepth(10);

    // Chest name
    const name = this.add.text(cx, height * 0.15, tierInfo.name.toUpperCase(), {
      fontFamily: 'monospace', fontSize: '20px',
      color: `#${tierInfo.color.toString(16).padStart(6, '0')}`,
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10).setAlpha(0);

    this.tweens.add({ targets: name, alpha: 1, duration: 300 });

    // Chest visual — real sprite, big, centered
    const chestY = height * 0.42;
    const tierKey = chest.tier.toLowerCase();
    const closedKey = `chest_${tierKey}_closed`;
    const openKey = `chest_${tierKey}_open`;

    // Closed chest sprite
    const chestSprite = this.add.image(cx, chestY + 80, closedKey)
      .setDepth(10).setAlpha(0).setScale(1.5);

    // Slide up
    this.tweens.add({
      targets: chestSprite, y: chestY, alpha: 1, duration: 400, ease: 'Back.easeOut',
      onComplete: () => {
        sfx(this, 'chest_appear');
        this.cameras.main.shake(40, 0.003);
      },
    });

    // Breathing
    this.tweens.add({
      targets: chestSprite, scaleX: 1.55, scaleY: 1.55,
      duration: 800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Click to open
    chestSprite.setInteractive({ useHandCursor: true });

    const hint = this.add.text(cx, chestY + 90, 'TAP TO OPEN', {
      fontFamily: 'monospace', fontSize: '12px', color: '#ffd700',
    }).setOrigin(0.5).setDepth(10).setAlpha(0);

    this.tweens.add({ targets: hint, alpha: 0.7, duration: 300, delay: 500 });
    this.tweens.add({ targets: hint, alpha: 0.3, duration: 500, yoyo: true, repeat: -1, delay: 800, ease: 'Sine.easeInOut' });

    chestSprite.on('pointerdown', () => {
      sfx(this, 'chest_shake');
      chestSprite.removeInteractive();
      hint.destroy();
      this.crackChest(chestSprite, chest, cx, chestY, counter, name, openKey);
    });
  }

  private crackChest(
    chestSprite: Phaser.GameObjects.Image,
    chest: Chest,
    x: number, y: number,
    counter: Phaser.GameObjects.Text,
    nameText: Phaser.GameObjects.Text,
    openKey: string
  ): void {
    // Shake chest
    this.tweens.add({
      targets: chestSprite, x: x - 4, duration: 40, yoyo: true, repeat: 4,
    });

    // THE PAUSE (0.6s)
    this.time.delayedCall(500, () => {
      const bestRarity = this.getBestRarity(chest.rewards);
      const burstColor = RARITY_COLORS[bestRarity];

      // CRACK — flash + shake
      sfx(this, 'chest_crack');
      this.cameras.main.flash(150, 255, 255, 255, false);
      this.cameras.main.shake(120, 0.008);

      // Switch to open chest sprite
      chestSprite.setTexture(openKey);

      // Chest opens then fades
      this.tweens.add({
        targets: chestSprite, scaleX: 1.8, scaleY: 1.8, duration: 200, ease: 'Back.easeOut',
        onComplete: () => {
          this.tweens.add({
            targets: chestSprite, alpha: 0, duration: 400, delay: 300,
          });
        },
      });

      // Particle burst from chest
      const burst = this.add.particles(x, y, 'chest_particle', {
        speed: { min: 50, max: 150 }, angle: { min: 230, max: 310 },
        scale: { start: 1.2, end: 0 }, alpha: { start: 1, end: 0 },
        lifespan: 500, quantity: 25,
        tint: [burstColor, 0xffffff, tierColorToInt(getChestTierInfo(chest.tier).color)],
        blendMode: 'ADD', emitting: false,
      }).setDepth(15);
      burst.explode(25);

      // Items fly out from chest position — VS style
      this.time.delayedCall(300, () => {
        this.showRewardsVS(chest.rewards, x, y, () => {
          // Cleanup
          counter.destroy();
          nameText.destroy();
          // Next chest
          this.currentChestIndex++;
          this.time.delayedCall(500, () => this.showNextChest());
        });
      });
    });
  }

  // ============ SINGLE REWARD REVEAL ============

  private showRewardsVS(rewards: ChestReward[], chestX: number, chestY: number, onDone: () => void): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const itemY = height * 0.55;

    // Single reward
    const reward = rewards[0];
    if (!reward) { onDone(); return; }

    if (reward.type === 'TOKENS' || reward.type === 'POINTS') {
      this.showTokenCounter(cx, itemY, chestX, chestY, reward, onDone);
    } else {
      this.showSkinReward(cx, itemY, chestX, chestY, reward, onDone);
    }
  }

  /** Token / Points reward — slot machine counter rolling up from 0.
   *
   *  POINTS: amount is a small integer (LB points) — counter shows "+N".
   *  TOKENS: amount is USD-micro on the wire (1 USD = 1e6). For display
   *  we convert to the native token at the live rate the server stamped,
   *  so the player sees "+500 SNK" — the thing they actually receive,
   *  not an internal accounting USD figure. Demo/offline flows fall back
   *  to USD since they have no oracle context. */
  private showTokenCounter(x: number, y: number, fromX: number, fromY: number, reward: ChestReward, onDone: () => void): void {
    const amount = reward.amount ?? 0;
    const isPoints = reward.type === 'POINTS';
    // Stake the player paid for this entry. Applies to POINTS display
    // for parity with HUD + coin pickup floater — a x10 player sees
    // "+50 POINTS" instead of "+5". Underlying amount is unchanged.
    // TOKENS display is computed off USD-micro (already absolute) so
    // multiplier doesn't apply there.
    const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;

    // Decide what to roll over and how to format ticks + final label.
    let counterMax: number;
    let formatRolling: (v: number) => string;
    let finalLabel: string;
    let secondaryLabel: string | null = null;
    let labelKicker: string;
    let labelColor: string;

    if (isPoints) {
      const displayAmount = amount * stake;
      counterMax = displayAmount;
      formatRolling = (v) => `+${v}`;
      finalLabel = `+${displayAmount}`;
      labelKicker = 'POINTS';
      labelColor = '#4488ff';
    } else if (this.chestPayout && BigInt(this.chestPayout.rateFemtoUsdcPerRaw) > 0n) {
      // Convert USD-micro → native raw using the server's stamped rate.
      // Same arithmetic the server does (round up to avoid shorting).
      const usdMicro = BigInt(amount);
      const rate = BigInt(this.chestPayout.rateFemtoUsdcPerRaw);
      const usdFemto = usdMicro * 1_000_000_000n;
      const tokenRaw = (usdFemto + rate - 1n) / rate;
      const symbol = this.chestPayout.symbol;
      const decimals = this.chestPayout.decimals;
      const human = Number(tokenRaw) / Math.pow(10, decimals);
      // Roll over whole-token units (capped at 99_999 so we don't tween
      // for 30 minutes if the token price tanks).
      counterMax = Math.max(1, Math.min(99_999, Math.floor(human)));
      const fmtHuman = (n: number) => {
        if (n === 0) return '0';
        if (n >= 1)    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
        if (n >= 0.01) return n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
        return n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
      };
      formatRolling = (v) => `+${v.toLocaleString()} ${symbol}`;
      finalLabel = `+${fmtHuman(human)} ${symbol}`;
      // Subtle USD reference under the main number — internal accounting
      // unit, just so people know it's stable.
      secondaryLabel = `≈ $${(amount / 1_000_000).toFixed(2)}`;
      labelKicker = this.chestPayout.granted ? 'REWARD' : 'RESERVE DEPLETED';
      labelColor = this.chestPayout.granted ? '#aa8800' : '#ff7171';
    } else {
      // Demo/offline fallback — no oracle context, show USD only.
      const usdCents = Math.round(amount / 10_000);
      counterMax = usdCents;
      formatRolling = (v) => `+$${(v / 100).toFixed(2)}`;
      finalLabel = `+$${(amount / 1_000_000).toFixed(2)}`;
      labelKicker = 'REWARD';
      labelColor = '#aa8800';
    }

    // Token icon flies up from chest
    const tokenIcon = this.add.text(x, fromY, '$', {
      fontFamily: 'monospace', fontSize: '32px', color: '#ffd700',
      stroke: '#000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(20).setAlpha(0).setScale(0.3);

    this.tweens.add({
      targets: tokenIcon, y: y - 25, alpha: 1, scaleX: 1, scaleY: 1,
      duration: 300, ease: 'Back.easeOut',
    });

    // Counter text — starts at 0, rolls up to final amount
    const counterText = this.add.text(x, y + 15, '0', {
      fontFamily: 'monospace', fontSize: '36px', color: '#ffd700',
      stroke: '#664400', strokeThickness: 3, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(20).setAlpha(0);

    const labelText = this.add.text(x, y + 45, labelKicker, {
      fontFamily: 'monospace', fontSize: '12px', color: labelColor,
    }).setOrigin(0.5).setDepth(20).setAlpha(0);

    // USD reference line (only when we showed a token amount above).
    const subText = secondaryLabel
      ? this.add.text(x, y + 62, secondaryLabel, {
          fontFamily: 'monospace', fontSize: '11px', color: '#7a7a8a',
        }).setOrigin(0.5).setDepth(20).setAlpha(0)
      : null;

    // Slot duration scales with counter ticks, capped so a big roll
    // doesn't take longer than the chest reveal lets us.
    const rollDuration = Math.min(2200, 1200 + counterMax * 5);

    this.time.delayedCall(400, () => {
      counterText.setAlpha(1);
      labelText.setAlpha(1);
      subText?.setAlpha(1);
      sfx(this, 'coins_rolling');

      this.tweens.addCounter({
        from: 0, to: counterMax,
        duration: rollDuration,
        ease: 'Cubic.easeOut',
        onUpdate: (tween) => {
          const v = Math.floor(tween.getValue() as number);
          counterText.setText(formatRolling(v));
          if (v % 5 === 0 && v < counterMax * 0.8) {
            counterText.setX(x + (Math.random() - 0.5) * 3);
          }
        },
        onComplete: () => {
          sfx(this, 'coins_punch');
          counterText.setX(x);
          counterText.setText(finalLabel);
          this.tweens.add({
            targets: counterText, scaleX: 1.3, scaleY: 1.3, duration: 100, yoyo: true,
          });
          this.cameras.main.shake(60, 0.004);
          this.cameras.main.flash(80, 255, 215, 0, false);
        },
      });
    });

    const totalTime = 400 + rollDuration + 800;
    this.time.delayedCall(totalTime, () => {
      const fadeTargets: any[] = [tokenIcon, counterText, labelText];
      if (subText) fadeTargets.push(subText);
      this.tweens.add({ targets: fadeTargets, alpha: 0, duration: 200 });
      this.time.delayedCall(300, onDone);
    });
  }

  /** Skin reward — pops out from chest */
  private showSkinReward(x: number, y: number, fromX: number, fromY: number, reward: ChestReward, onDone: () => void): void {
    const rarityInfo = getRarityInfo(reward.rarity);
    const isLegendary = reward.rarity === 'LEGENDARY';
    const w = 160;
    const h = 84;
    if (reward.type === 'HAT') {
      markHatOwned(reward.id);
      // Server-granted rewards are already persisted by SinglePlayerRoom.
      // Only fallback/local rolls need to call the compatibility endpoint.
      if (!this.rewardsServerGranted) void this.persistHatUnlock(reward);
    }

    const container = this.add.container(fromX, fromY).setDepth(20).setScale(0.3).setAlpha(0);

    // Background pill
    const bg = this.add.rectangle(0, 0, w, h, 0x0e0e1e, 0.95)
      .setStrokeStyle(2, rarityInfo.color);
    container.add(bg);

    // Rarity label
    const rarityLabel = this.add.text(-w / 2 + 10, -h / 2 + 10, rarityInfo.label.toUpperCase(), {
      fontFamily: 'monospace', fontSize: '8px',
      color: `#${rarityInfo.color.toString(16).padStart(6, '0')}`,
      fontStyle: 'bold',
    });
    container.add(rarityLabel);

    // Item name
    const name = this.add.text(0, -4, reward.name, {
      fontFamily: 'monospace', fontSize: '14px', color: '#ffffff',
      fontStyle: isLegendary ? 'bold' : 'normal',
    }).setOrigin(0.5);
    container.add(name);

    // Slot type
    const slot = this.add.text(0, 14, reward.slot ?? '', {
      fontFamily: 'monospace', fontSize: '10px',
      color: `#${rarityInfo.color.toString(16).padStart(6, '0')}`,
    }).setOrigin(0.5);
    container.add(slot);

    // "Added to wardrobe" confirmation
    const wallet = (window as any).__currentUser?.walletAddress;
    const wardrobeText = wallet ? '+ ADDED TO WARDROBE' : '(LOGIN TO KEEP)';
    const wardrobeColor = wallet ? '#6ee7ff' : '#666677';
    const added = this.add.text(0, h / 2 - 10, wardrobeText, {
      fontFamily: 'monospace', fontSize: '8px', color: wardrobeColor,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(added);

    // Pop out
    this.tweens.add({
      targets: container,
      x: x, y: y,
      scaleX: 1, scaleY: 1,
      alpha: 1,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        sfx(this, 'reward_skin_reveal');
        this.cameras.main.shake(30, 0.002);
        if (reward.rarity === 'RARE' || isLegendary) {
          this.cameras.main.flash(80, ...this.hexToRgb(rarityInfo.color), false);
        }
        if (isLegendary) {
          this.time.delayedCall(100, () => this.legendaryBurst(x, y, reward));
        }
      },
    });

    // Done after hold
    const holdTime = isLegendary ? 3000 : 1200;
    this.time.delayedCall(holdTime, () => {
      this.tweens.add({ targets: container, alpha: 0, duration: 200 });
      this.time.delayedCall(300, onDone);
    });
  }

  /** Extra ceremony for legendary items */
  private legendaryBurst(x: number, y: number, reward: ChestReward): void {
    sfx(this, 'reward_legendary');
    const { width, height } = this.scale;

    this.cameras.main.flash(200, 255, 215, 0, false);
    this.cameras.main.shake(200, 0.01);

    // "LEGENDARY!" text
    const text = this.add.text(width / 2, height * 0.2, 'LEGENDARY!', {
      fontFamily: 'monospace', fontSize: '32px', color: '#ffd700',
      stroke: '#664400', strokeThickness: 4, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(30).setScale(1.8).setAlpha(0);

    this.tweens.add({
      targets: text, alpha: 1, scale: 1, duration: 250, ease: 'Back.easeOut',
    });

    // Gold particles
    const particles = this.add.particles(x, y, 'chest_particle', {
      speed: { min: 20, max: 60 }, angle: { min: 0, max: 360 },
      scale: { start: 0.8, end: 0 }, alpha: { start: 0.7, end: 0 },
      lifespan: { min: 600, max: 1200 }, frequency: 40, quantity: 2,
      tint: [0xffd700, 0xffffff, 0xff8800],
      blendMode: 'ADD',
    }).setDepth(25);

    // Clean up after 1.5s
    this.time.delayedCall(1500, () => {
      this.tweens.add({ targets: text, alpha: 0, duration: 200, onComplete: () => text.destroy() });
      particles.destroy();
    });
  }

  // ============ HOOKS ============

  private showHooks(): void {
    const { width, height } = this.scale;
    const cx = width / 2;

    // Save carryover coins to localStorage with 24h expiry
    const leftoverCoins = this.result.coinsRemaining ?? 0;
    if (leftoverCoins > 0 && this.result.nextChestThreshold > 0) {
      const expiryTime = Date.now() + 24 * 60 * 60 * 1000; // 24 hours
      localStorage.setItem('chest_carryover', JSON.stringify({
        coins: leftoverCoins,
        nextThreshold: this.result.nextChestThreshold,
        expiresAt: expiryTime,
      }));
    }

    // Clear everything
    this.children.each((child: Phaser.GameObjects.GameObject) => {
      this.tweens.add({ targets: child, alpha: 0, duration: 200 });
    });

    this.time.delayedCall(300, () => {
      // Background
      this.add.rectangle(cx, height / 2, width, height, 0x030308).setDepth(0);

      let hookY = height * 0.25;

      // Carryover coins + urgency message
      if (leftoverCoins > 0 && this.result.coinsToNextChest > 0) {
        // Progress saved
        const savedText = this.add.text(cx, hookY, `${leftoverCoins} COINS SAVED TOWARD NEXT CHEST`, {
          fontFamily: 'monospace', fontSize: '15px', color: '#ffd700',
          stroke: '#000', strokeThickness: 3, fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(10);

        hookY += 30;

        // Progress bar
        const barW = 280;
        const progress = Math.min(1, leftoverCoins / this.result.nextChestThreshold);
        this.add.rectangle(cx, hookY, barW, 12, 0x1a1a2e).setDepth(10);
        const fill = this.add.rectangle(cx - barW / 2, hookY, 0, 12, 0xffd700).setOrigin(0, 0.5).setDepth(10);
        this.tweens.add({ targets: fill, width: barW * progress, duration: 800, ease: 'Power2' });

        // Threshold label
        this.add.text(cx + barW / 2 + 10, hookY, `${this.result.nextChestThreshold}`, {
          fontFamily: 'monospace', fontSize: '10px', color: '#888899',
        }).setOrigin(0, 0.5).setDepth(10);

        hookY += 30;

        // URGENCY — expires soon, pulsing red
        const urgency = this.add.text(cx, hookY, '⚠ EXPIRES IN 24H — DON\'T MISS IT', {
          fontFamily: 'monospace', fontSize: '12px', color: '#ff4444',
          stroke: '#000', strokeThickness: 2,
        }).setOrigin(0.5).setDepth(10);

        this.tweens.add({
          targets: urgency, alpha: 0.4, duration: 800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        });

        hookY += 20;

        // How many more coins needed
        this.add.text(cx, hookY, `NEED ${this.result.coinsToNextChest} MORE COINS`, {
          fontFamily: 'monospace', fontSize: '11px', color: '#888899',
        }).setOrigin(0.5).setDepth(10);

        hookY += 35;
      } else if (this.result.chests.length === 0) {
        this.add.text(cx, hookY, 'NO CHESTS THIS TIME', {
          fontFamily: 'monospace', fontSize: '16px', color: '#666677',
        }).setOrigin(0.5).setDepth(10);

        hookY += 25;

        this.add.text(cx, hookY, `NEED ${this.result.coinsToNextChest} COINS TO EARN A CHEST`, {
          fontFamily: 'monospace', fontSize: '12px', color: '#888899',
        }).setOrigin(0.5).setDepth(10);

        hookY += 35;
      }

      // Crypto chart (3rd extraction only — shows full match history)
      if (this.coinChart && this.extractionsUsed >= 3) {
        const chartW = Math.min(260, width * 0.55);
        const chartContainer = this.coinChart.render(this, cx, hookY + 15, chartW, 90, 10);
        hookY += 110;
      } else {
        // Stats summary (shown for partial extractions)
        const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;
        const displayCoins = this.result.coinsBanked * stake;
        this.add.text(cx, hookY + 15, `COINS: ${displayCoins}  |  KILLS: ${this.result.kills}  |  CHESTS: ${this.result.chests.length}`, {
          fontFamily: 'monospace', fontSize: '11px', color: '#555566',
        }).setOrigin(0.5).setDepth(10);
      }

      // Leaderboard points earned — multiplied by stake for display parity
      // with the HUD ("+1,000 LEADERBOARD POINTS" if you paid x10 with 100
      // raw banked). The actual write to match_results.leaderboard_points
      // is server-side coinsBanked × stake, so this number matches what
      // the all-time LB will show.
      if (this.result.totalLeaderboardPoints > 0) {
        const stake = (this.registry.get('stakeMultiplier') as number | undefined) ?? 1;
        const displayPoints = this.result.totalLeaderboardPoints * stake;
        const pointsText = this.add.text(cx, hookY + 40, `+${displayPoints} LEADERBOARD POINTS`, {
          fontFamily: 'monospace', fontSize: '13px', color: '#ffd700',
          stroke: '#000', strokeThickness: 2, fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(10).setAlpha(0);

        this.tweens.add({ targets: pointsText, alpha: 1, duration: 400 });
        this.tweens.add({
          targets: pointsText, scaleX: 1.05, scaleY: 1.05,
          duration: 500, yoyo: true, repeat: 2, ease: 'Sine.easeInOut',
        });
      }

      const hasLives = this.extractionsUsed < 3;

      if (hasLives) {
        // Show extractions used
        const livesText = this.add.text(cx, height * 0.64, `◆ EXTRACTION ${this.extractionsUsed}/3 ◆`, {
          fontFamily: 'monospace', fontSize: '14px', color: '#00ff88',
          stroke: '#000', strokeThickness: 2,
        }).setOrigin(0.5).setDepth(10).setAlpha(0);
        this.tweens.add({ targets: livesText, alpha: 1, duration: 400, delay: 200 });

        // "ENTER AGAIN" button
        const enterBtn = this.add.text(cx, height * 0.72, `[ ENTER AGAIN — ${3 - this.extractionsUsed} LEFT ]`, {
          fontFamily: 'monospace', fontSize: '22px', color: '#00ff88',
          stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(10).setAlpha(0).setInteractive({ useHandCursor: true });

        this.tweens.add({ targets: enterBtn, alpha: 1, duration: 400, delay: 400 });
        this.tweens.add({
          targets: enterBtn, scale: 1.05, duration: 600, yoyo: true, repeat: -1, delay: 800, ease: 'Sine.easeInOut',
        });

        enterBtn.on('pointerover', () => { sfx(this, 'button_hover'); enterBtn.setColor('#ffffff'); });
        enterBtn.on('pointerout', () => enterBtn.setColor('#00ff88'));
        enterBtn.on('pointerdown', () => {
          sfx(this, 'enter_again');
          this.cameras.main.fadeOut(300, 0, 0, 0);
          this.cameras.main.once('camerafadeoutcomplete', () => {
            if (this.returnToGame) {
              // Tell server to respawn the snake, then resume GameScene
              this.game.events.emit('requestReenter');
              this.scene.stop('ChestCeremonyScene');
              this.scene.setVisible(true, 'GameScene');
              this.scene.resume('GameScene');
              this.scene.launch('HUDScene');
            } else {
              // Final extraction — new run requires new perk + new entry fee
              this.scene.start('PerkSelectScene');
            }
          });
        });
      } else {
        // No lives left — need new life
        const noLivesText = this.add.text(cx, height * 0.64, 'ALL 3 EXTRACTIONS USED', {
          fontFamily: 'monospace', fontSize: '14px', color: '#ff4444',
          stroke: '#000', strokeThickness: 2,
        }).setOrigin(0.5).setDepth(10).setAlpha(0);
        this.tweens.add({ targets: noLivesText, alpha: 1, duration: 400 });

        // "NEW LIFE" button (future: costs money)
        const newLifeBtn = this.add.text(cx, height * 0.72, '[ NEW LIFE ]', {
          fontFamily: 'monospace', fontSize: '22px', color: '#ffd700',
          stroke: '#000', strokeThickness: 5, fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(10).setAlpha(0).setInteractive({ useHandCursor: true });

        this.tweens.add({ targets: newLifeBtn, alpha: 1, duration: 400, delay: 300 });
        this.tweens.add({
          targets: newLifeBtn, scale: 1.05, duration: 600, yoyo: true, repeat: -1, delay: 700, ease: 'Sine.easeInOut',
        });

        newLifeBtn.on('pointerover', () => { sfx(this, 'button_hover'); newLifeBtn.setColor('#ffffff'); });
        newLifeBtn.on('pointerout', () => newLifeBtn.setColor('#ffd700'));
        newLifeBtn.on('pointerdown', () => {
          sfx(this, 'new_life');
          this.cameras.main.fadeOut(300, 0, 0, 0);
          this.cameras.main.once('camerafadeoutcomplete', () => {
            // New life = new perk select
            this.scene.start('PerkSelectScene', { perks: getRandomPerks(3) });
          });
        });
      }

      // Back to menu option (small)
      const quitBtn = this.add.text(cx, height * 0.85, 'Back to Menu', {
        fontFamily: 'monospace', fontSize: '12px', color: '#444455',
      }).setOrigin(0.5).setDepth(10).setInteractive({ useHandCursor: true });

      quitBtn.on('pointerdown', () => {
        sfx(this, 'button_click');
        // Cancel re-entry slot and disconnect
        this.game.events.emit('requestCancelReentry');
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.stop('GameScene');
          this.scene.start('MainMenuScene');
        });
      });
    });
  }

  // ============ HELPERS ============

  private getBestRarity(rewards: ChestReward[]): ItemRarity {
    const order: ItemRarity[] = ['COMMON', 'UNCOMMON', 'RARE', 'LEGENDARY'];
    let best = 0;
    for (const r of rewards) {
      const idx = order.indexOf(r.rarity);
      if (idx > best) best = idx;
    }
    return order[best];
  }

  private hexToRgb(hex: number): [number, number, number] {
    return [(hex >> 16) & 0xff, (hex >> 8) & 0xff, hex & 0xff];
  }

  /** POST a hat unlock to the server inventory. No-op for non-wallet users.
   *  Also marks the hat as owned in the local cache immediately so future
   *  chest rolls don't dupe it before the next inventory refresh. */
  private async persistHatUnlock(reward: ChestReward): Promise<void> {
    if (reward.type !== 'HAT') return;
    const wallet = (window as any).__currentUser?.walletAddress;
    if (!wallet) return;
    try {
      const timestamp = Date.now();
      const signature = await this.signHatUnlock(wallet, reward.id, timestamp);
      await fetch(`${SERVER_HTTP_URL}/api/players/me/hats/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress: wallet,
          hatId: reward.id,
          signature,
          timestamp,
        }),
      });
    } catch {
      // Network blip — drop silently. Next chest unlock will retry.
    }
  }

  private async signHatUnlock(wallet: string, hatId: string, timestamp: number): Promise<string> {
    const provider = getProvider();
    if (!provider) throw new Error('No wallet detected');
    if (!provider.publicKey) await provider.connect();
    if (provider.publicKey?.toString() !== wallet) {
      throw new Error('Connected wallet does not match current user');
    }

    const message = `Pump My Snake — unlock hat ${hatId} at ${timestamp}`;
    const { signature } = await provider.signMessage(new TextEncoder().encode(message));
    return btoa(String.fromCharCode(...signature));
  }
}

function tierColorToInt(color: number): number {
  return color;
}
